import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Plus, Grid3x3 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { resolveAssetUrl } from "@/lib/assetUrl";
import { toast } from "sonner";

/**
 * My Boards — user's saved-pin collections. Public boards are shareable.
 */
export default function BoardsPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/join?returnTo=/blinterest/boards");
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/blinterest/boards`, { withCredentials: true })
      .then((r) => setBoards(r.data.boards || []))
      .catch(() => toast.error("Couldn't load your Collections"))
      .finally(() => setLoading(false));
  }, [user]);

  const createBoard = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const r = await axios.post(
        `${API}/blinterest/boards`,
        { name, privacy: "public" },
        { withCredentials: true },
      );
      setBoards((prev) => [r.data, ...prev]);
      setNewName("");
      setCreateOpen(false);
      toast.success(`Collection "${name}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create Collection");
    } finally {
      setCreating(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-tbc-navy" data-testid="boards-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl text-white">My Collections</h1>
            <p className="text-white/60 font-body text-sm mt-1">
              Every Find you&apos;ve kept, organized your way.
            </p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full"
                data-testid="create-board-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Start a Collection
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-tbc-navy text-white border-white/10">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-white">Start a Collection</DialogTitle>
              </DialogHeader>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Hair Goals, Dream Home, Restaurants to Try"
                className="bg-white/10 border-white/20 text-white h-11"
                data-testid="board-name-input"
              />
              <Button
                onClick={createBoard}
                disabled={creating}
                className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full h-11"
                data-testid="board-create-submit"
              >
                {creating ? "Creating…" : "Create Collection"}
              </Button>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="aspect-square bg-white/5 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : boards.length === 0 ? (
          <div className="text-center py-16 text-white/60">
            <Grid3x3 className="w-14 h-14 mx-auto mb-4 text-white/30" />
            <p className="font-heading text-2xl text-white mb-2">No Collections yet</p>
            <p className="font-body text-sm mb-6">
              Your first Collection starts with one great Find.
            </p>
            <Link to="/blinterest">
              <Button className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full">
                Start Discovering
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {boards.map((b) => (
              <Link
                key={b.board_id}
                to={`/blinterest/board/${b.board_id}`}
                className="block group"
                data-testid={`board-card-${b.board_id}`}
              >
                <div className="aspect-square rounded-2xl overflow-hidden bg-white/5 border border-white/10 group-hover:border-tbc-gold/40 transition-colors">
                  {b.cover_image ? (
                    <img
                      src={resolveAssetUrl(b.cover_image)}
                      alt={b.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/30">
                      <Grid3x3 className="w-10 h-10" />
                    </div>
                  )}
                </div>
                <p className="text-white font-body text-sm mt-2 truncate">{b.name}</p>
                <p className="text-white/50 text-xs">{b.pin_count || 0} Find{b.pin_count === 1 ? "" : "s"}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
